import { useMemo, useState } from 'react'

import { capitalizeWords } from '~/helpers/capitalize-words'
import { DEFAULT_SELECTED_SUBPUBLISHER_CATALOG } from '~/modules/retailMedia/constants/subpublisherCatalogs'

export function useSubpublisherCatalogFilter({
  selectedSubpublishers,
  allSubpublishers,
  selected,
  setSelected,
  resetQuery,
  handleTabs,
  activeTab,
  campaignType
}: SubpublisherCatalogFilterProps) {
  const [searchValue, setSearchValue] = useState('')

  /**
   * Format select options
   */
  const subpublishers: CheckboxOptionProps[] = useMemo(() => {
    const publishers = allSubpublishers.filter(item =>
      selectedSubpublishers?.includes(item.id)
    )

    const formattedPublishers = publishers?.map(item => ({
      id: item.id,
      value: item.id,
      name: item.id,
      label: capitalizeWords(item.name)
    }))

    return formattedPublishers
  }, [allSubpublishers, selectedSubpublishers])

  // Filter options based on search input
  const filteredSubpublishers = useMemo(() => {
    if (!searchValue) return subpublishers
    return subpublishers.filter(subpublisher =>
      subpublisher.label
        .toString()
        .toLowerCase()
        .includes(searchValue.toLowerCase())
    )
  }, [searchValue, subpublishers])

  // Translate selected value to label
  function handleTranslateToLabel(): string {
    if (selected === DEFAULT_SELECTED_SUBPUBLISHER_CATALOG.id) {
      return DEFAULT_SELECTED_SUBPUBLISHER_CATALOG.label
    } else {
      const subpublisher = subpublishers?.find(item => item.id === selected)
      return (subpublisher?.label as string) || ''
    }
  }

  const value = useMemo(() => {
    return selected ? handleTranslateToLabel() : ''
  }, [selected])

  // Handle select change
  function handleSetValue(value: string) {
    const subpublisher = subpublishers?.find(item => item?.label === value)
    const valueToSet = subpublisher
      ? subpublisher?.id
      : DEFAULT_SELECTED_SUBPUBLISHER_CATALOG.id
    setSelected(valueToSet)
    resetQuery()

    if (
      campaignType !== 'off_site' &&
      handleTabs &&
      activeTab === 'product_gtins'
    )
      handleTabs('product_skus')
  }

  return {
    filteredSubpublishers,
    value,
    handleSetValue,
    searchValue,
    setSearchValue
  }
}
